import React from "react";
import lodashGet from "lodash/get";
import { useSelector } from "react-redux";
//material ui
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
//charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/pie";
import "echarts/lib/chart/gauge";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";
//icons
import ExcelIcon from "mdi-material-ui/FileExcel";
import ArrowBack from "@material-ui/icons/ArrowBack";
//jazasoft
import { PageFooter, Button } from "jazasoft";
//local
import Report from "./Report";
import CardHeader from "../../../components/CardHeader";
//helpers
import { getDistinctValues, humanizeNumber, isEmpty, isTrue, toFixedValue } from "../../../utils/helpers";
import { DataType, getSummaryEchartOptions } from "../../../helpers/reportHelper";

import { MacroReportId, UOM } from "../../../constants";
import { useDeepMemo, useMutation, useSetting } from "../../../hooks";
import { makeStyles } from "@material-ui/core";
import SettingKeys from "../../../utils/SettingKeys";
import MuiTable from "../../../components/MuiTable";
import { AlertParemeter } from "../../../utils/types";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const headerStyle1 = { bold: true, bgColor: "19, 96, 164", color: "255,255,255" };
const headerStyle2 = { bold: true, bgColor: "0,98,5", color: "255,255,255" };

const columns1 = [
  { dataKey: "orderQty", title: "Order Qty" },
  { dataKey: "netCutQty", title: "Net Cut Qty" }, // cut-rejected
  { dataKey: "rejectedQty", title: "Rejected Qty" },
  { dataKey: "allocated", title: "Fabric Allocated", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "issued", title: "Fabric Issued", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "returned", title: "Returned to W/H", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "leftover", title: "Gross Leftover", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "netIssued", title: "Net Issued", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "blanketQty", title: "Testing Loss", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "totalConsumed", title: "Total Consumed", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` }
];

const columns2 = [
  { dataKey: "totalConsumed", title: "Total \nConsumed", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  {
    dataKey: "shortageExcess",
    title: "Net \nShortage/Excess",
    render: (value, _, row) => (isEmpty(value) ? "NA" : `${value} ${UomCodeFromValue[row.fabricUom] || ""}`)
  },
  { dataKey: "netConsumed", title: "Net \nConsumed", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "usedInMarker", title: "Used \nin Marker", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "endLoss", title: "End Loss", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "grossEndBits", title: "Gross \nEnd Bits", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  {
    dataKey: "endBitsUsedInPanel",
    title: "Panel Recut \n(End Bits)",
    render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}`
  },
  { dataKey: "netEndBits", title: "Net \nEnd Bits", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "rollUsedInPanel", title: "Panel Recut \n(Roll)", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "damaged", title: "Fabric \nDamage", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "netWastage", title: "Net \nWastage", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "missing", title: "Missed \nin Cutting", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "unused", title: "Unutilized \nby Cutting", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "blanketQty", title: "Testing Loss", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` },
  { dataKey: "grossWastage", title: "Gross \nWastage*", render: (value, _, row) => `${value} ${UomCodeFromValue[row.fabricUom] || ""}` }
];

const filterKeys = ["start", "end", "fabricUom", "unitIds", "season", "product", "fabricPattern", "fabricCategory", "supplier", "customer"];

const useStyles = makeStyles(theme => ({
  root: {},
  card: {
    margin: "1em 0"
  },
  cardContent: {
    padding: 0,
    overflowX: "auto"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  }
}));

const AggregateReconciliationReportView = props => {
  const { orderList, history } = props;
  const classes = useStyles();
  const i18nMessages = useSelector(state => state.i18n?.messages || {});
  const fabricUom = props.filter?.fabricUom || UOM.METER.value;

  const [download] = useMutation({ rawResponse: true });

  const showStdCuVal = useSetting(SettingKeys.SHOW_STD_CU);
  const showStdCu = isTrue(showStdCuVal);

  const onDownload = () => {
    const fileName = "FCR_REPORT.xlsx";

    const reportList = orderList.map(r => {
      const netCutQty = r?.cutQty - (r?.rejectedQty || 0);
      const highlightList = !isEmpty(r.highlights) ? JSON.parse(r.highlights) : [];
      const shrinkageVariation = getDistinctValues(
        highlightList
          .filter(h => [AlertParemeter.WARP_SHRINKAGE_VARIATION.id, AlertParemeter.WEFT_SHRINKAGE_VARIATION.id].includes(h.parameter))
          .flatMap(h => h.reasons || [])
      ).join(", ");
      const highlights = highlightList.reduce((acc, h) => ({ ...acc, [h.parameter]: h.reasons?.join(",") }), { shrinkageVariation });

      if (fabricUom === UOM.METER.value) {
        return {
          ...r,
          ...highlights,
          netCutQty,
          netCutPercent: r.orderQty > 0 ? toFixedValue((r.cutQty - r.rejectedQty) / r.orderQty, 3) : 0.0,
          actualCuDiff: (r.actualCu - r.orderingCu) * 100,
          grossCuDiff: (r.grossCu - r.orderingCu) * 100,
          leftover: toFixedValue(r.allocated + r.returned - r.issued, 1),
          netIssued: toFixedValue(r.issued - r.returned, 1),
          shortageExcess: r.shortage + r.excess,
          usedInMarker: r.usedInLay - r.endLoss,
          usedInPanelRemnant: r.grossEndBits - r.netEndBits,
          usedInPanelRoll: r.usedInPanel - (r.grossEndBits - r.netEndBits),
          netWastage: r.endLoss + r.netEndBits + r.usedInPanel + r.damaged,
          grossWastage: r.endLoss + r.netEndBits + r.usedInPanel + r.damaged + r.missing + r.unused + r.blanketQty,
          fabRepeat:
            !isEmpty(r.fabricPattern) && r.fabricPattern?.toLowerCase() !== "solid"
              ? isEmpty(r.repeatLength) && isEmpty(r.repeatWidth)
                ? ""
                : isEmpty(r.repeatLength)
                  ? `0 X ${r.repeatWidth}`
                  : isEmpty(r.repeatWidth)
                    ? `${r.repeatLength} X 0`
                    : `${r.repeatLength} X ${r.repeatWidth}`
              : null,
          markerEfficiency: toFixedValue(r.markerEfficiency / 100, 4),
          widthDiff: toFixedValue(r.actualWidth - r.orderingWidth, 1)
        };
      } else {
        return {
          ...r,
          ...highlights,
          netCutQty,
          orderingCu: r.orderingCuInKg,
          stdCu: r.stdCuInKg,
          planCu: r.planCuInKg,
          actualCu: r.actualCuInKg,
          grossCu: r.grossCuInKg,
          netCutPercent: r.orderQty > 0 ? toFixedValue((r.cutQty - r.rejectedQty) / r.orderQty, 3) : 0.0,
          actualCuDiff: (r.actualCuInKg - r.orderingCuInKg) * 1000,
          grossCuDiff: (r.grossCuInKg - r.orderingCuInKg) * 1000,
          allocated: r.allocatedWeight,
          blanketQty: r.blanketWeight,
          issued: r.issuedWeight,
          leftover: toFixedValue(r.allocatedWeight + r.returnedWeight - r.issuedWeight, 1),
          netIssued: toFixedValue(r.issuedWeight - r.returnedWeight, 1),
          shortageExcess: 0,
          endLoss: r.endLossWeight,
          grossEndBits: r.grossEndBitsWeight,
          netEndBits: r.netEndBitsWeight,
          usedInMarker: r.usedInLayWeight - r.endLossWeight,
          usedInPanel: r.usedInPanelWeight,
          damaged: r.damagedWeight,
          missing: r.missingWeight,
          unused: r.unusedWeight,
          usedInPanelRemnant: r.grossEndBitsWeight - r.netEndBitsWeight,
          usedInPanelRoll: r.usedInPanelWeight - (r.grossEndBitsWeight - r.netEndBitsWeight),
          netWastage: r.endLossWeight + r.netEndBitsWeight + r.usedInPanelWeight + r.damagedWeight,
          grossWastage:
            r.endLossWeight + r.netEndBitsWeight + r.usedInPanelWeight + r.damagedWeight + r.missingWeight + r.unusedWeight + r.blanketWeight,
          fabRepeat:
            !isEmpty(r.fabricPattern) && r.fabricPattern?.toLowerCase() !== "solid"
              ? isEmpty(r.repeatLength) && isEmpty(r.repeatWidth)
                ? ""
                : isEmpty(r.repeatLength)
                  ? `0 X ${r.repeatWidth}`
                  : isEmpty(r.repeatWidth)
                    ? `${r.repeatLength} X 0`
                    : `${r.repeatLength} X ${r.repeatWidth}`
              : null,
          markerEfficiency: toFixedValue(r.markerEfficiency / 100, 4),
          widthDiff: toFixedValue(r.actualWidth - r.orderingWidth, 1)
        };
      }
    });

    let columns = [
      { dataKey: "unitIds", title: "Unit", headerStyle: headerStyle1 },
      { dataKey: "cuttingStartDate", title: "Cutting Start Date", type: "date", headerStyle: headerStyle1 },
      { dataKey: "cuttingClosedDate", title: "Cutting Close Date", type: "date", headerStyle: headerStyle1 },
      { dataKey: "orderCloseDate", title: "Close Date", type: "date", headerStyle: headerStyle1 },
      { dataKey: "poRef", title: "ERP No", headerStyle: headerStyle1 },
      { dataKey: "customer", title: "Buyer", headerStyle: headerStyle1 },
      { dataKey: "season", title: "Season", headerStyle: headerStyle1 },
      { dataKey: "style", title: "Style", headerStyle: headerStyle1 },
      { dataKey: "styleNo", title: "Style No", headerStyle: headerStyle1 },
      { dataKey: "color", title: "Style Color", headerStyle: headerStyle1 },
      { dataKey: "part", title: "Part", headerStyle: headerStyle1 },
      { dataKey: "placement", title: "Placement", headerStyle: headerStyle1 },

      { dataKey: "orderQty", title: "Order Qty", headerStyle: headerStyle2 },
      { dataKey: "fpoQty", title: "FPO Qty", headerStyle: headerStyle2 },
      { dataKey: "netCutQty", title: "Net Cut Quantity", headerStyle: headerStyle2 },
      { dataKey: "rejectedQty", title: "Rejected Qty", headerStyle: headerStyle2 },
      { dataKey: "netCutPercent", title: "Net Cut (%)", cellStyle: { dataFormat: "0.0%" }, headerStyle: headerStyle2 },

      { dataKey: "orderingCu", title: "BOM CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
      { dataKey: "stdCu", title: "Std. CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
      { dataKey: "planCu", title: "Plan CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
      { dataKey: "actualCu", title: " Actual CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
      { dataKey: "grossCu", title: "Gross CU", type: "double", cellStyle: { dataFormat: "0.000" }, headerStyle: headerStyle1 },
      { dataKey: "actualCuDiff", title: "BOM vs Actual", type: "double", cellStyle: { dataFormat: "0.0" }, headerStyle: headerStyle1 },
      { dataKey: "grossCuDiff", title: "BOM vs Gross", type: "double", cellStyle: { dataFormat: "0.0" }, headerStyle: headerStyle1 },

      { dataKey: "markerEfficiency", title: "Marker Efficiency", cellStyle: { dataFormat: "0.00%" }, headerStyle: headerStyle2 },
      { dataKey: "fabDirection", title: "Fab Direction", headerStyle: headerStyle2 },
      { dataKey: "csv", title: "CSV", headerStyle: headerStyle2 },
      { dataKey: "fabricPattern", title: "Fab Pattern", headerStyle: headerStyle2 },
      { dataKey: "fabRepeat", title: "Fab Repeat", headerStyle: headerStyle2 },
      { dataKey: "orderingWidth", title: "Fab Bom Width (in CM)", headerStyle: headerStyle2 },
      { dataKey: "actualWidth", title: "Fab Actual Width (in CM)", headerStyle: headerStyle2 },
      { dataKey: "widthDiff", title: "Width Deviation", headerStyle: headerStyle2 },
      { dataKey: "actualWarpShrinkage", title: "Actual Warp Shrinkage", headerStyle: headerStyle2 },
      { dataKey: "actualWeftShrinkage", title: "Actual Weft Shrinkage", headerStyle: headerStyle2 },

      { dataKey: "fabricOrderQty", title: "Fabric Required Qty", headerStyle: headerStyle1 },
      { dataKey: "associationQty", title: "Fabric Associated", headerStyle: headerStyle1 },
      { dataKey: "allocated", title: "Fabric Allocated", headerStyle: headerStyle1 },
      { dataKey: "blanketQty", title: "Testing Loss", headerStyle: headerStyle1 },
      { dataKey: "issued", title: "Fabric Issued", headerStyle: headerStyle1 },
      { dataKey: "returned", title: "Returned", headerStyle: headerStyle1 },
      { dataKey: "leftover", title: "Gross Leftover", headerStyle: headerStyle1 },

      { dataKey: "netIssued", title: "Net Issued", headerStyle: headerStyle2 },
      { dataKey: "shortageExcess", title: "Shortage/Excess", headerStyle: headerStyle2 },
      { dataKey: "usedInMarker", title: "Used in Marker", headerStyle: headerStyle2 },
      { dataKey: "endLoss", title: "End Loss", headerStyle: headerStyle2 },
      { dataKey: "usedInLayManual", title: "Used in Recutting Lay", headerStyle: headerStyle2 },
      { dataKey: "grossEndBits", title: "Gross End Bits", headerStyle: headerStyle2 },
      { dataKey: "usedInPanelRemnant", title: "Panel Recut (End Bits)", headerStyle: headerStyle2 },
      { dataKey: "netEndBits", title: "Net End Bits", headerStyle: headerStyle2 },
      { dataKey: "usedInPanelRoll", title: "Panel Recut (Roll)", headerStyle: headerStyle2 },
      { dataKey: "usedInPanel", title: "Total Panel Recut", headerStyle: headerStyle2 },
      { dataKey: "damaged", title: "Fabric Damaged", headerStyle: headerStyle2 },
      { dataKey: "netWastage", title: "Net Wastage", headerStyle: headerStyle2 },
      { dataKey: "missing", title: "Missed", headerStyle: headerStyle2 },
      { dataKey: "unused", title: "Unused", headerStyle: headerStyle2 },
      { dataKey: "grossWastage", title: "Gross Wastage", headerStyle: headerStyle2 },

      { dataKey: "itemCode", title: "Item Code", headerStyle: headerStyle1 },
      { dataKey: "articleName", title: "Article Name", headerStyle: headerStyle1 },
      { dataKey: "fabColor", title: "Item Color", headerStyle: headerStyle1 },
      { dataKey: "supplier", title: "Supplier", headerStyle: headerStyle1 },

      { dataKey: "customerCode", title: "Customer Code", headerStyle: headerStyle2 },
      { dataKey: "merchant", title: "Merchandiser", headerStyle: headerStyle2 },
      { dataKey: "customerFlowRefs", title: "BPO", headerStyle: headerStyle2 },
      { dataKey: "orderQtySizeBreakup", title: "Order Qty Size Breakup", headerStyle: headerStyle2 },
      { dataKey: "cutQtySizeBreakup", title: "Cut Qty Size Breakup", headerStyle: headerStyle2 },

      { dataKey: AlertParemeter.CUT_PLAN_QTY.id, title: "Cut Plan Qty \ndeviation Reasons", headerStyle: headerStyle1 },
      { dataKey: AlertParemeter.CONSUMPTION_VARIATION.id, title: "Consumption Variation \ndeviation Reasons", headerStyle: headerStyle1 },
      { dataKey: AlertParemeter.MARKER_EFFICIENCY_VARIATION.id, title: "Marker Efficiency \ndeviation Reasons", headerStyle: headerStyle1 },
      { dataKey: AlertParemeter.CUTTABLE_WIDTH_VARIATION.id, title: "Width \ndeviation Reasons", headerStyle: headerStyle1 },
      { dataKey: AlertParemeter.GSM_VARIATION.id, title: "GSM \ndeviation Reasons", headerStyle: headerStyle1 },
      { dataKey: AlertParemeter.CSV_VARIATION.id, title: "CSV \ndeviation Reasons", headerStyle: headerStyle1 },
      { dataKey: "shrinkageVariation", title: "Shrinkage \ndeviation Reasons", headerStyle: headerStyle1 },
      { dataKey: "higherEndBits", title: "High End Bits \nReasons", headerStyle: headerStyle1 },
      { dataKey: "higherRecut", title: "High Recutting \nReasons", headerStyle: headerStyle1 },
      { dataKey: "fabricLeftover", title: "Fabric Leftover \nReasons", headerStyle: headerStyle1 }
    ].map(c => ({ ...c, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));

    if (!showStdCu) {
      columns = columns.filter(c => c.dataKey !== "stdCu");
    }
    if (props.orderList?.every(e => isEmpty(e.styleNo))) {
      columns = columns.filter(c => c.dataKey !== "styleNo");
    }

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [
        {
          sheetNo: 0,
          sheetName: "RECONCILIATION REPORT",
          autoSizeColumn: false,
          excelData: {
            columnList: columns.map((c, idx) => ({ ...c, colNo: idx + 1 })),
            rowList: reportList.map((row, idx) => ({
              rowNo: idx + 1,
              cellList: columns.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
            }))
          }
        }
      ],
      params: { filename: fileName }
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var filename = header ? header.match(/filename="(.+)"/)[1] : "FCR_REPORT.xlsx";

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
      }
    });
  };

  const reportList = props.orderList.map(e => ({
    ...e,
    shortageExcess: e.shortage + e.excess,
    available: e.allocated + e.excess + e.shortage - (e.returned + e.missing + e.unused),
    usedInLayPlanned: isEmpty(e.usedInLayDetails) ? 0 : JSON.parse(e.usedInLayDetails).PLANNED,
    usedInLayRecutting: isEmpty(e.usedInLayDetails) ? 0 : JSON.parse(e.usedInLayDetails).RE_CUTTING
  }));

  const rows1 = useDeepMemo(() => {
    if (isEmpty(reportList)) return [];

    const orderQty = reportList.reduce((acc, e) => acc + (e.orderQty || 0), 0);
    const cutQty = reportList.reduce((acc, e) => acc + (e.cutQty || 0), 0);
    const rejectedQty = reportList.reduce((acc, e) => acc + (e.rejectedQty || 0), 0);
    const blanketQty = reportList.reduce((acc, e) => acc + (e.blanketQty || 0), 0);
    const allocated = reportList.reduce((acc, e) => acc + (e.allocated || 0), 0);
    const issued = reportList.reduce((acc, e) => acc + (e.issued || 0), 0);
    const returned = reportList.reduce((acc, e) => acc + (e.returned || 0), 0);

    const blanketWeight = reportList.reduce((acc, e) => acc + (e.blanketWeight || 0), 0);
    const allocatedWeight = reportList.reduce((acc, e) => acc + (e.allocatedWeight || 0), 0);
    const issuedWeight = reportList.reduce((acc, e) => acc + (e.issuedWeight || 0), 0);
    const returnedWeight = reportList.reduce((acc, e) => acc + (e.returnedWeight || 0), 0);

    const netCutQty = cutQty - (rejectedQty || 0);

    const leftover = allocated + returned - issued;
    const netIssued = issued - returned;
    const totalConsumed = issued - returned + blanketQty;

    const leftoverWeight = allocatedWeight + returnedWeight - issuedWeight;
    const netIssuedWeight = issuedWeight - returnedWeight;
    const totalConsumedWeight = issuedWeight - returnedWeight + blanketWeight;

    return [
      fabricUom === UOM.KILOGRAM.value
        ? {
            fabricUom,
            orderQty: humanizeNumber(orderQty),
            netCutQty: humanizeNumber(netCutQty),
            rejectedQty: humanizeNumber(rejectedQty || 0),
            blanketQty: humanizeNumber(toFixedValue(blanketWeight, 1)),
            allocated: humanizeNumber(toFixedValue(allocatedWeight, 1)),
            issued: humanizeNumber(toFixedValue(issuedWeight, 1)),
            returned: humanizeNumber(toFixedValue(returnedWeight, 1)),
            leftover: humanizeNumber(toFixedValue(leftoverWeight, 1)),
            netIssued: humanizeNumber(toFixedValue(netIssuedWeight, 1)),
            totalConsumed: humanizeNumber(toFixedValue(totalConsumedWeight, 1))
          }
        : {},
      fabricUom === UOM.METER.value
        ? {
            fabricUom: UOM.METER.value,
            orderQty: humanizeNumber(orderQty),
            netCutQty: humanizeNumber(netCutQty),
            rejectedQty: humanizeNumber(rejectedQty || 0),
            blanketQty: humanizeNumber(toFixedValue(blanketQty, 1)),
            allocated: humanizeNumber(toFixedValue(allocated, 1)),
            issued: humanizeNumber(toFixedValue(issued, 1)),
            returned: humanizeNumber(toFixedValue(returned, 1)),
            leftover: humanizeNumber(toFixedValue(leftover, 1)),
            netIssued: humanizeNumber(toFixedValue(netIssued, 1)),
            totalConsumed: humanizeNumber(toFixedValue(totalConsumed, 1))
          }
        : {},
      {
        orderQty: "100.0%",
        netCutQty: orderQty > 0 ? `${toFixedValue((netCutQty * 100) / orderQty, 1)}%` : "0.0%",
        rejectedQty: netCutQty > 0 ? `${toFixedValue((rejectedQty * 100) / netCutQty, 1)}%` : "0.0%",
        allocated: "100.0%",
        issued: allocated > 0 ? `${toFixedValue((issued * 100) / allocated, 2)}%` : "0.0%",
        returned: allocated > 0 ? `${toFixedValue((returned * 100) / allocated, 2)}%` : "0.0%",
        leftover: allocated > 0 ? `${toFixedValue((leftover * 100) / allocated, 2)}%` : "0.0%",
        netIssued: allocated > 0 ? `${toFixedValue((netIssued * 100) / allocated, 2)}%` : "0.0%",
        blanketQty: allocated + blanketQty > 0 ? `${toFixedValue((blanketQty * 100) / (allocated + blanketQty), 2)}%` : "0.0%",
        totalConsumed: allocated + blanketQty > 0 ? `${toFixedValue((totalConsumed * 100) / (allocated + blanketQty), 2)}%` : "0.0%"
      }
    ].filter(e => !isEmpty(e));
  }, [reportList, fabricUom]);

  const rows2 = useDeepMemo(() => {
    if (isEmpty(reportList)) return [];

    const blanketQty = reportList.reduce((acc, e) => acc + (e.blanketQty || 0), 0);
    const issued = reportList.reduce((acc, e) => acc + (e.issued || 0), 0);
    const returned = reportList.reduce((acc, e) => acc + (e.returned || 0), 0);
    const usedInLay = reportList.reduce((acc, e) => acc + (e.usedInLay || 0), 0);
    const endLoss = reportList.reduce((acc, e) => acc + (e.endLoss || 0), 0);
    const grossEndBits = reportList.reduce((acc, e) => acc + (e.grossEndBits || 0), 0);
    const netEndBits = reportList.reduce((acc, e) => acc + (e.netEndBits || 0), 0);
    const damaged = reportList.reduce((acc, e) => acc + (e.damaged || 0), 0);
    const usedInPanel = reportList.reduce((acc, e) => acc + (e.usedInPanel || 0), 0);
    const missing = reportList.reduce((acc, e) => acc + (e.missing || 0), 0);
    const unused = reportList.reduce((acc, e) => acc + (e.unused || 0), 0);
    const shortageExcess = reportList.reduce((acc, e) => acc + (e.shortageExcess || 0), 0);
    const netWastage = endLoss + netEndBits + damaged + usedInPanel;
    const grossWastage = netWastage + missing + unused + blanketQty;

    const totalConsumed = issued + blanketQty - returned;
    const netConsumed = totalConsumed + shortageExcess;
    const netIssued = issued - returned;
    const endBitsUsedInPanel = grossEndBits - netEndBits;
    const rollUsedInPanel = usedInPanel - endBitsUsedInPanel;

    const blanketWeight = reportList.reduce((acc, e) => acc + (e.blanketWeight || 0), 0);
    const issuedWeight = reportList.reduce((acc, e) => acc + (e.issuedWeight || 0), 0);
    const returnedWeight = reportList.reduce((acc, e) => acc + (e.returnedWeight || 0), 0);
    const usedInLayWeight = reportList.reduce((acc, e) => acc + (e.usedInLayWeight || 0), 0);
    const endLossWeight = reportList.reduce((acc, e) => acc + (e.endLossWeight || 0), 0);
    const grossEndBitsWeight = reportList.reduce((acc, e) => acc + (e.grossEndBitsWeight || 0), 0);
    const netEndBitsWeight = reportList.reduce((acc, e) => acc + (e.netEndBitsWeight || 0), 0);
    const damagedWeight = reportList.reduce((acc, e) => acc + (e.damagedWeight || 0), 0);
    const usedInPanelWeight = reportList.reduce((acc, e) => acc + (e.usedInPanelWeight || 0), 0);
    const missingWeight = reportList.reduce((acc, e) => acc + (e.missingWeight || 0), 0);
    const unusedWeight = reportList.reduce((acc, e) => acc + (e.unusedWeight || 0), 0);

    const netWastageWeight = endLossWeight + netEndBitsWeight + damagedWeight + usedInPanelWeight;
    const grossWastageWeight = netWastageWeight + missingWeight + unusedWeight + blanketWeight;

    const totalConsumedWeight = issuedWeight + blanketWeight - returnedWeight;
    const netConsumedWeight = totalConsumedWeight + 0;
    const endBitsUsedInPanelWeight = grossEndBitsWeight - netEndBitsWeight;
    const rollUsedInPanelWeight = usedInPanelWeight - endBitsUsedInPanelWeight;
    return [
      fabricUom === UOM.KILOGRAM.value
        ? {
            fabricUom,
            totalConsumed: humanizeNumber(toFixedValue(totalConsumedWeight, 1)),
            shortageExcess: undefined,
            netConsumed: humanizeNumber(toFixedValue(netConsumedWeight, 1)),
            usedInMarker: humanizeNumber(toFixedValue(usedInLayWeight - endLossWeight, 1)),
            endLoss: humanizeNumber(toFixedValue(endLossWeight, 1)),
            netEndBits: humanizeNumber(toFixedValue(netEndBitsWeight, 1)),
            endBitsUsedInPanel: humanizeNumber(toFixedValue(endBitsUsedInPanelWeight, 1)),
            grossEndBits: humanizeNumber(toFixedValue(grossEndBitsWeight, 1)),
            damaged: humanizeNumber(toFixedValue(damagedWeight, 1)),
            rollUsedInPanel: humanizeNumber(toFixedValue(rollUsedInPanelWeight, 1)),
            netWastage: humanizeNumber(toFixedValue(netWastageWeight, 1)),
            missing: humanizeNumber(toFixedValue(missingWeight, 1)),
            unused: humanizeNumber(toFixedValue(unusedWeight, 1)),
            blanketQty: humanizeNumber(toFixedValue(blanketWeight, 1)),
            grossWastage: humanizeNumber(toFixedValue(grossWastageWeight, 1))
          }
        : {},
      fabricUom === UOM.METER.value
        ? {
            fabricUom: UOM.METER.value,
            totalConsumed: humanizeNumber(toFixedValue(totalConsumed, 1)),
            shortageExcess: fabricUom === UOM.KILOGRAM.value ? undefined : humanizeNumber(toFixedValue(shortageExcess, 1)),
            netConsumed: humanizeNumber(toFixedValue(netConsumed, 1)),
            usedInMarker: humanizeNumber(toFixedValue(usedInLay - endLoss, 1)),
            endLoss: humanizeNumber(toFixedValue(endLoss, 1)),
            netEndBits: humanizeNumber(toFixedValue(netEndBits, 1)),
            endBitsUsedInPanel: humanizeNumber(toFixedValue(endBitsUsedInPanel, 1)),
            grossEndBits: humanizeNumber(toFixedValue(grossEndBits, 1)),
            damaged: humanizeNumber(toFixedValue(damaged, 1)),
            rollUsedInPanel: humanizeNumber(toFixedValue(rollUsedInPanel, 1)),
            netWastage: humanizeNumber(toFixedValue(netWastage, 1)),
            missing: humanizeNumber(toFixedValue(missing, 1)),
            unused: humanizeNumber(toFixedValue(unused, 1)),
            blanketQty: humanizeNumber(toFixedValue(blanketQty, 1)),
            grossWastage: humanizeNumber(toFixedValue(grossWastage, 1))
          }
        : {},
      {
        totalConsumed: "100.0%",
        shortageExcess:
          fabricUom === UOM.KILOGRAM.value ? undefined : netIssued > 0 ? `${toFixedValue((shortageExcess * 100) / totalConsumed, 2)}%` : "0.0%",
        netConsumed: "100.0%",
        usedInMarker: netConsumed > 0 ? `${toFixedValue(((usedInLay - endLoss) * 100) / netConsumed, 2)}%` : "0.0%",
        endLoss: netConsumed > 0 ? `${toFixedValue((endLoss * 100) / netConsumed, 2)}%` : "0.0%",
        grossEndBits: netConsumed > 0 ? `${toFixedValue((grossEndBits * 100) / netConsumed, 2)}%` : "0.0%",
        endBitsUsedInPanel: netConsumed > 0 ? `${toFixedValue((endBitsUsedInPanel * 100) / netConsumed, 2)}%` : "0.0%",
        netEndBits: netConsumed > 0 ? `${toFixedValue((netEndBits * 100) / netConsumed, 2)}%` : "0.0%",
        rollUsedInPanel: netConsumed > 0 ? `${toFixedValue((rollUsedInPanel * 100) / netConsumed, 2)}%` : "0.0%",
        damaged: netConsumed > 0 ? `${toFixedValue((damaged * 100) / netConsumed, 2)}%` : "0.0%",
        usedInPanel: netConsumed > 0 ? `${toFixedValue((usedInPanel * 100) / netConsumed, 2)}%` : "0.0%",
        netWastage: netConsumed > 0 ? `${toFixedValue((netWastage * 100) / netConsumed, 2)}%` : "0.0%",
        missing: netConsumed > 0 ? `${toFixedValue((missing * 100) / netConsumed, 2)}%` : "0.0%",
        unused: netConsumed > 0 ? `${toFixedValue((unused * 100) / netConsumed, 2)}%` : "0.0%",
        blanketQty: netConsumed > 0 ? `${toFixedValue((blanketQty * 100) / netConsumed, 2)}%` : "0.0%",
        grossWastage: netConsumed > 0 ? `${toFixedValue((grossWastage * 100) / netConsumed, 2)}%` : "0.0%"
      }
    ].filter(e => !isEmpty(e));
  }, [reportList, fabricUom]);

  const options = getSummaryEchartOptions({
    reportList,
    dataTypeList: [DataType.FABRIC_UTILIZATION_SUMMARY, DataType.WASTAGE_SUMMMARY, DataType.CONSUMPTION_SUMMARY],
    uom: fabricUom
  });

  if (reportList.length === 0) {
    return (
      <div style={{ marginTop: "3em" }}>
        <Typography className={classes.displayCenter} color="primary" variant="subtitle1">
          Data not available
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div style={{ marginTop: "1em" }}>
        <MuiTable toolbar title={"Fabric Summary"} columns={columns1} rows={rows1} />
      </div>

      <div style={{ marginTop: "1em" }}>
        <MuiTable title={"Fabric Used Details"} toolbar columns={columns2} rows={rows2} />
      </div>

      <Grid id="row-3" container spacing={2} style={{ marginTop: "1em" }}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.border}>
            <CardHeader title="Fabric Utilization Summary" />
            <CardContent style={{ padding: "0 0 8px 0" }}>
              <ReactEchartsCore
                echarts={echarts}
                option={options[DataType.FABRIC_UTILIZATION_SUMMARY]}
                style={{ height: "250px", width: "100%" }}
                opts={{ renderer: "svg" }}
                className="react_for_echarts"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.border}>
            <CardHeader title="Wastage Summary" />
            <CardContent style={{ padding: "0 0 8px 0" }}>
              <ReactEchartsCore
                echarts={echarts}
                option={options[DataType.WASTAGE_SUMMMARY]}
                style={{ height: "250px", width: "100%" }}
                opts={{ renderer: "svg" }}
                className="react_for_echarts"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.border}>
            <CardHeader title="Consumtion Summary" />
            <CardContent style={{ padding: "0 0 8px 16px" }}>
              <ReactEchartsCore
                echarts={echarts}
                option={options[DataType.CONSUMPTION_SUMMARY]}
                style={{ height: "250px", width: "100%" }}
                opts={{ renderer: "svg" }}
                className="react_for_echarts"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PageFooter className={classes.displayCenter}>
        <Button label="Back" variant="contained" onClick={() => history.goBack()}>
          <ArrowBack />
        </Button>
        <Button label="Download" variant="contained" style={{ color: "green", marginLeft: "1.5em" }} onClick={onDownload}>
          <ExcelIcon />
        </Button>
      </PageFooter>
    </div>
  );
};

const AggregateReconciliationReport = props => (
  <Report
    title="FCR Report"
    filterKeys={filterKeys}
    dataEndpoint="report-summary"
    defaultMonths={3}
    macroReportId={MacroReportId.AGGREGATE_RECONCILIATION}
    {...props}>
    <AggregateReconciliationReportView {...props} />
  </Report>
);

export default AggregateReconciliationReport;
